import type {
  AttributionController_staff_v1Query,
  AttributionController_terminals_v1Query,
  AttributionRateResource,
} from '@repo/api-client';

/**
 * What the two attribution reads can be ranked by, and how a rate is put into
 * words.
 *
 * ## The reads rank; they do not judge
 *
 * The API publishes **no verdict of any kind**. There is no `suspicious`, no
 * `flag`, no `score`, no `risk`, no `threshold` and no `anomaly` anywhere in
 * either resource, and `blossom-rest-api`'s `test/reporting-contract.e2e-spec.ts`
 * walks the schemas and the mappers and fails that build if one is ever added
 * (CL-902). The route's own document says it in as many words: *there is nothing
 * here that asserts a figure is unusual — the set is measures, and a person
 * reads the ranking.*
 *
 * **That decision is inherited here**, and it is easier to undo in a client than
 * in a schema: a red row, a warning triangle, a column headed "worst", a
 * hard-coded `rate > 0.1`. None of those appears in this module or in the screen
 * beside it, and `Attribution.test.tsx` asserts their absence twice — once
 * against what renders and once against this source.
 *
 * The reason is not squeamishness. A staff member accused by a screen the API
 * was deliberately designed not to let accuse them is the outcome the whole
 * shape of these two resources was chosen to prevent.
 *
 * ## The denominator is half the fact
 *
 * `AttributionRateResource` carries `numerator`, `denominator` and `rate`, and
 * the document is explicit that the denominator is *the figure that makes the
 * rate comparable to another one*. `{numerator: 1, denominator: 1, rate: 1}` is
 * one refund out of one sale — a quiet Tuesday. It sorts to the top of a refund
 * ranking and it is not the worst case in the park. Rendered as `100%` alone it
 * reads as an accusation, so the two figures are formatted separately here and
 * shown together, always, by everything that renders one.
 *
 * A null `rate` is not nought. It means the denominator was zero and the measure
 * was never computed — publishing `0%` would rank somebody who recorded no sale
 * as the best-behaved person in the park. It says "No rate" instead.
 */

type StaffMeasure = NonNullable<AttributionController_staff_v1Query['orderBy']>;
type TerminalMeasure = NonNullable<AttributionController_terminals_v1Query['orderBy']>;

/** One thing a list can be ranked by: the value the route takes, and its label. */
export interface AttributionMeasure<Value extends string> {
  readonly value: Value;
  readonly label: string;
}

/**
 * The seven measures the staff read accepts, in the order its document lists
 * them.
 *
 * Typed against the operation's own query, so a measure the route does not
 * accept is a compile error rather than a `400` at the till end of a Tuesday.
 * The labels name the measure and nothing else — none of them says whether a
 * figure is good.
 */
export const STAFF_MEASURES: readonly AttributionMeasure<StaffMeasure>[] = [
  { value: 'refundRate', label: 'Refund rate' },
  { value: 'voidRate', label: 'Void rate' },
  { value: 'reprintRate', label: 'Reprint rate' },
  { value: 'refundedValue', label: 'Value handed back' },
  { value: 'takings', label: 'Takings' },
  { value: 'transactions', label: 'Transactions' },
  { value: 'averageTransactionValue', label: 'Average transaction' },
];

/**
 * The six the terminal read accepts.
 *
 * **No refund measure, and its absence is the contract rather than an
 * oversight.** `sale_refunds` carries no `terminal_id`, so no refund in this
 * park can be grouped by till (CL-904, `blossom-rest-api#421`); the route
 * refuses `refundRate` with a `400` rather than answering it with zeroes, and
 * offering it here would send a request the screen could already see was
 * refused.
 */
export const TERMINAL_MEASURES: readonly AttributionMeasure<TerminalMeasure>[] = [
  { value: 'voidRate', label: 'Void rate' },
  { value: 'reprintRate', label: 'Reprint rate' },
  { value: 'voidedValue', label: 'Value voided' },
  { value: 'takings', label: 'Takings' },
  { value: 'transactions', label: 'Transactions' },
  { value: 'averageTransactionValue', label: 'Average transaction' },
];

/**
 * What each list opens ranked by.
 *
 * **Takings, and deliberately not a rate.** Either list would be a defensible
 * default; opening on `refundRate` would mean the first thing an owner ever sees
 * is people ordered by the measure most easily misread as an accusation, chosen
 * by the screen rather than asked for. Takings is what "how did the park trade"
 * means, and a reader who wants a rate picks one.
 */
export const DEFAULT_STAFF_MEASURE: StaffMeasure = 'takings';
export const DEFAULT_TERMINAL_MEASURE: TerminalMeasure = 'takings';

/**
 * A measure's label, from the list it belongs to.
 *
 * Base UI resolves what a `<SelectValue>` shows from the root's `items` rather
 * than from the rendered options, and this is the same lookup by another name —
 * so the trigger, the options and any prose about the ranking all read one list.
 */
export function measureLabel<Value extends string>(
  measures: readonly AttributionMeasure<Value>[],
  value: Value,
): string {
  return measures.find((measure) => measure.value === value)?.label ?? value;
}

/** What is shown when there was no denominator to divide by. */
const NO_RATE = 'No rate';

/**
 * A rate as a percentage, to one decimal place.
 *
 * One decimal rather than none: three refunds in four hundred sales is 0.75%,
 * and rounded to a whole number it is 1% — which is a third again as large as
 * the figure the server sent.
 *
 * **Not clamped at 100%.** A reprint rate can exceed one, because a reprint of
 * somebody else's receipt is still their act and the document says the rate
 * *says so rather than hiding it*. A screen that capped it would be quietly
 * disagreeing with the number it was given.
 */
export function formatRatePercent({ rate }: AttributionRateResource): string {
  if (rate === null) return NO_RATE;

  return `${(rate * 100).toFixed(1)}%`;
}

/** The fraction the percentage came from, shown beside it and never without it. */
export function formatRateFraction({ numerator, denominator }: AttributionRateResource): string {
  return `${String(numerator)} / ${String(denominator)}`;
}

/**
 * The same rate as one sentence, for a reader who is listening rather than
 * looking.
 *
 * The visible cell sets the percentage over its fraction, which reads as
 * "5.0% 10 / 198" when a screen reader runs the two together — the slash is
 * announced inconsistently and "10 / 198" beside a percentage is ambiguous
 * enough to be worth saying properly once.
 */
export function describeRate(rate: AttributionRateResource): string {
  if (rate.rate === null) return `${NO_RATE} — nothing to measure it over`;

  return `${formatRatePercent(rate)}, ${String(rate.numerator)} out of ${String(rate.denominator)}`;
}
